import React from 'react';
import type { OrderFill, Order } from '../types';
import { toEAT } from '../utils/time';

interface FillHistoryTableProps {
  fills: OrderFill[];
  order: Order;
}

function formatTimestamp(time: string): string {
  if (!time) return '—';
  try {
    const date = new Date(time);
    if (!isNaN(date.getTime())) {
      return toEAT(date);
    }
    return time;
  } catch {
    return time;
  }
}

export default function FillHistoryTable({ fills, order }: FillHistoryTableProps) {
  if (fills.length === 0) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', color: '#7e8a96', fontSize: 13 }}>
        No fills yet
      </div>
    );
  }
  
  const totalValue = fills.reduce((sum, f) => sum + (f.value || (f.quantity * f.price)), 0);
  const totalFilled = fills.reduce((sum, f) => sum + f.quantity, 0);
  
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ 
        marginBottom: 8, 
        fontSize: 12, 
        fontWeight: 600, 
        color: '#d7dde4' 
      }}>
        Fill History ({fills.length} {fills.length === 1 ? 'fill' : 'fills'})
      </div>
      
      <div style={{
        border: '1px solid #2a3540',
        borderRadius: 6,
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(120px,1.5fr) minmax(84px,1fr) minmax(84px,1fr) minmax(84px,1fr) minmax(84px,1fr) minmax(90px,1.1fr)',
          gap: 8,
          padding: '8px 12px',
          backgroundColor: '#0a0d10',
          borderBottom: '1px solid #2a3540',
          fontSize: 11,
          color: '#7e8a96',
          fontWeight: 600
        }}>
          <div>Time</div>
          <div style={{ textAlign: 'right' }}>Quantity</div>
          <div style={{ textAlign: 'right' }}>Price</div>
          <div style={{ textAlign: 'right' }}>Cumulative</div>
          <div style={{ textAlign: 'right' }}>Remaining</div>
          <div style={{ textAlign: 'right' }}>Value</div>
        </div>
        
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {fills.map((fill, index) => {
            const isLatest = index === fills.length - 1;
            return (
              <div
                key={fill.fillId || `${fill.time}_${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px,1.5fr) minmax(84px,1fr) minmax(84px,1fr) minmax(84px,1fr) minmax(84px,1fr) minmax(90px,1.1fr)',
                  gap: 8,
                  padding: '8px 12px',
                  backgroundColor: isLatest ? '#10151b' : 'transparent',
                  borderBottom: index < fills.length - 1 ? '1px solid #2a3540' : 'none',
                  fontSize: 11,
                  fontFamily: isLatest ? 'monospace' : 'inherit',
                  fontWeight: isLatest ? 600 : 400,
                  transition: 'background-color 0.2s'
                }}
              >
                <div style={{ color: isLatest ? '#ff8c3a' : '#7e8a96' }}>
                  {formatTimestamp(fill.time)}
                </div>
                <div style={{ textAlign: 'right', color: '#d7dde4' }}>
                  {fill.quantity.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', color: '#d7dde4' }}>
                  {fill.price.toFixed(2)}
                </div>
                <div style={{ textAlign: 'right', color: '#7e8a96' }}>
                  {fill.cumQty.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', color: '#7e8a96' }}>
                  {fill.leavesQty.toLocaleString()}
                </div>
                <div style={{ textAlign: 'right', color: '#d7dde4' }}>
                  {(fill.value || (fill.quantity * fill.price)).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div style={{
        marginTop: 12,
        padding: '12px',
        backgroundColor: '#0a0d10',
        borderRadius: 6,
        border: '1px solid #2a3540',
        fontSize: 12,
        color: '#7e8a96'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span>Total Filled:</span>
          <span style={{ color: '#d7dde4', fontWeight: 600 }}>{totalFilled.toLocaleString()} / {order.qty.toLocaleString()}</span>
        </div>
        {order.avgFillPrice !== undefined && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span>Average Fill Price:</span>
            <span style={{ color: '#d7dde4', fontWeight: 600 }}>{order.avgFillPrice ? order.avgFillPrice.toFixed(2) : '—'}</span>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Total Value:</span>
          <span style={{ color: '#d7dde4', fontWeight: 600 }}>
            {totalValue.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </span>
        </div>
      </div>
    </div>
  );
}

